const FALLBACK_CONTACT = {
  address_lines: ["Floraweg 199", "1032 ZG Amsterdam", "The Netherlands"],
  phone: "+31 687 519 025",
  fax: "+31 847 359 280",
  emails: ["info@petvankort.com", "admin@petvankort.com"],
  office_region: "Amsterdam office serving clients with UK and EU legal reach.",
  consultation_note:
    "Consultations are available by appointment. Personal injury and workers' compensation matters include free initial consultations.",
};

const FALLBACK_CONTENT = {
  firm_name: "Peters Van Kort",
  hero_overline: "25 years of decisive advocacy",
  hero_title: "Cross-border legal strategy for complex disputes and immigration matters.",
  hero_subtitle: "General law practitioner • Immigration lawyer • UK & EU case wins",
  hero_description:
    "The Law Firm of Peters Van Kort combines meticulous preparation, forceful advocacy, and personal attention to help clients navigate civil disputes, workplace conflicts, immigration concerns, and high-stakes litigation across borders.",
  hero_image: "https://petvankort.com/picttures/banner.jpg",
  architecture_image:
    "https://images.pexels.com/photos/3137086/pexels-photo-3137086.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  navigation: [
    { label: "Home", path: "/" },
    { label: "About", path: "/about" },
    { label: "Practice Areas", path: "/services" },
    { label: "Contact", path: "/contact" },
  ],
  marquee_items: [
    "Immigration law",
    "General civil litigation",
    "Employment disputes",
    "Personal injury",
    "Workers' compensation",
    "Civil rights",
  ],
  stats: [
    {
      value: "25+",
      label: "Years of experience",
      detail: "Established practice shaped by decades of courtroom and negotiation work.",
    },
    {
      value: "UK + EU",
      label: "Case success footprint",
      detail: "Trusted on disputes and outcomes spanning the United Kingdom and Europe.",
    },
    {
      value: "1000s",
      label: "Matters handled",
      detail: "A proven history across injury, employment, civil rights, and business disputes.",
    },
    {
      value: "1994",
      label: "Current practice founded",
      detail: "Built after legal work in Washington, D.C. and San Jose before returning to Amsterdam.",
    },
  ],
  profile_summary: [
    "The original firm profile describes a highly experienced civil litigation practice committed to quality legal representation delivered efficiently and with compassion.",
    "That foundation continues today through a broader profile that also highlights general law and immigration support for clients facing complex, high-pressure legal problems.",
    "Every matter is prepared with the discipline of a trial case so clients gain leverage in settlement, mediation, arbitration, or court.",
  ],
  about_heading: "Prepared like trial counsel. Trusted like a long-term adviser.",
  about_intro:
    "Mr. Peters Van Kort has built a reputation for aggressive preparation, strategic judgment, and respectful client care.",
  about_story: [
    "After graduating from law school in 1986 and working with established law firms in Washington, D.C., and San Jose, California, Mr. Peters Van Kort returned to Amsterdam and launched his current practice in 1994.",
    "His experience spans wrongful death, serious personal injury, workers' compensation, insurance bad faith, labor and employment disputes, discrimination, contracts, business conflicts, intellectual property, construction defect, property matters, and real estate disputes.",
    "The firm's philosophy remains pragmatic and results-oriented: prepare every case thoroughly, pursue prompt resolution where possible, and litigate decisively when justice demands it.",
  ],
  attorney_name: "Mr. Peters Van Kort",
  attorney_summary: [
    "For more than two decades, Mr. Peters Van Kort has represented clients in demanding civil disputes while refining an approach rooted in clarity, persistence, and preparation.",
    "He is committed to achieving successful results for clients and is especially known for handling complex matters that involve overlapping legal issues, multiple forums, and difficult negotiations.",
    "His profile now brings together general law practice, immigration guidance, and a record of wins across the UK and EU — reinforcing the international perspective clients often need.",
  ],
  credentials: [
    "Juris Doctor from UCLA",
    "B.A. degrees in Philosophy and Economics from UCLA",
    "Licensed before Federal and State courts in the USA and the Netherlands",
    "Member of the State Bar of California",
    "Member of the Dutch Association for the Judiciary",
    "Member of Monterey County Bar Association, CAAA, and AAJ",
  ],
  services: [
    {
      title: "Immigration law",
      description: "Strategic legal guidance for individuals and families navigating immigration pathways, documentation, and complex cross-border legal concerns.",
      highlight: "Clear direction in high-stakes transitions",
    },
    {
      title: "General civil litigation",
      description: "Representation for contractual disputes, property matters, business conflicts, and civil claims that demand disciplined preparation and negotiation strength.",
      highlight: "Prepared for resolution or trial",
    },
    {
      title: "Employment and labor disputes",
      description: "Support for wage issues, discrimination, harassment, civil rights violations, and employment-related disputes with a client-first approach.",
      highlight: "Focused advocacy for employees and professionals",
    },
    {
      title: "Injury and compensation claims",
      description: "From personal injury and wrongful death to workers' compensation and third-party claims, every case is handled with care and urgency.",
      highlight: "Compassion backed by relentless preparation",
    },
  ],
  timeline: [
    "1986 — Law school completed and early career established in Washington, D.C. and San Jose.",
    "1994 — Current Amsterdam practice founded with a litigation-first philosophy.",
    "25+ years — Sustained record across civil disputes, employment issues, injury claims, and cross-border legal matters.",
    "Today — A premium, client-centered profile spanning general law, immigration, and UK/EU case success.",
  ],
  contact: FALLBACK_CONTACT,
  footer_statement:
    "Let's talk about the matter in front of you — and the outcome you want behind it.",
};

const asNonEmptyString = (value, fallback = "") =>
  typeof value === "string" && value.trim() ? value.trim() : fallback;

const asStringArray = (value, fallback = []) =>
  Array.isArray(value) && value.length
    ? value.filter((item) => typeof item === "string" && item.trim()).map((item) => item.trim())
    : fallback;

const asObjectArray = (value, fallback = []) => (Array.isArray(value) && value.length ? value : fallback);

export const normalizeSiteContent = (rawContent) => {
  const safeContent = rawContent && typeof rawContent === "object" ? rawContent : {};
  const safeContact = safeContent.contact && typeof safeContent.contact === "object" ? safeContent.contact : {};

  const navigation = asObjectArray(safeContent.navigation, FALLBACK_CONTENT.navigation).map((item, index) => ({
    label: asNonEmptyString(item?.label, FALLBACK_CONTENT.navigation[index]?.label || `Link ${index + 1}`),
    path: asNonEmptyString(item?.path, FALLBACK_CONTENT.navigation[index]?.path || "/"),
  }));

  const stats = asObjectArray(safeContent.stats, FALLBACK_CONTENT.stats).map((item, index) => ({
    value: asNonEmptyString(item?.value, FALLBACK_CONTENT.stats[index]?.value || "—"),
    label: asNonEmptyString(item?.label, FALLBACK_CONTENT.stats[index]?.label || `Stat ${index + 1}`),
    detail: asNonEmptyString(item?.detail, FALLBACK_CONTENT.stats[index]?.detail || ""),
  }));

  const services = asObjectArray(safeContent.services, FALLBACK_CONTENT.services).map((item, index) => ({
    title: asNonEmptyString(item?.title, FALLBACK_CONTENT.services[index]?.title || `Service ${index + 1}`),
    description: asNonEmptyString(
      item?.description,
      FALLBACK_CONTENT.services[index]?.description || "Support tailored to your legal matter.",
    ),
    highlight: asNonEmptyString(
      item?.highlight,
      FALLBACK_CONTENT.services[index]?.highlight || "Legal support",
    ),
  }));

  const contact = {
    address_lines: asStringArray(safeContact.address_lines, FALLBACK_CONTACT.address_lines),
    phone: asNonEmptyString(safeContact.phone, FALLBACK_CONTACT.phone),
    fax: asNonEmptyString(safeContact.fax, FALLBACK_CONTACT.fax),
    emails: asStringArray(safeContact.emails, FALLBACK_CONTACT.emails),
    office_region: asNonEmptyString(safeContact.office_region, FALLBACK_CONTACT.office_region),
    consultation_note: asNonEmptyString(
      safeContact.consultation_note,
      FALLBACK_CONTACT.consultation_note,
    ),
  };

  return {
    firm_name: asNonEmptyString(safeContent.firm_name, FALLBACK_CONTENT.firm_name),
    hero_overline: asNonEmptyString(safeContent.hero_overline, FALLBACK_CONTENT.hero_overline),
    hero_title: asNonEmptyString(safeContent.hero_title, FALLBACK_CONTENT.hero_title),
    hero_subtitle: asNonEmptyString(safeContent.hero_subtitle, FALLBACK_CONTENT.hero_subtitle),
    hero_description: asNonEmptyString(safeContent.hero_description, FALLBACK_CONTENT.hero_description),
    hero_image: asNonEmptyString(safeContent.hero_image, FALLBACK_CONTENT.hero_image),
    architecture_image: asNonEmptyString(
      safeContent.architecture_image,
      FALLBACK_CONTENT.architecture_image,
    ),
    navigation,
    marquee_items: asStringArray(safeContent.marquee_items, FALLBACK_CONTENT.marquee_items),
    stats,
    profile_summary: asStringArray(safeContent.profile_summary, FALLBACK_CONTENT.profile_summary),
    about_heading: asNonEmptyString(safeContent.about_heading, FALLBACK_CONTENT.about_heading),
    about_intro: asNonEmptyString(safeContent.about_intro, FALLBACK_CONTENT.about_intro),
    about_story: asStringArray(safeContent.about_story, FALLBACK_CONTENT.about_story),
    attorney_name: asNonEmptyString(safeContent.attorney_name, FALLBACK_CONTENT.attorney_name),
    attorney_summary: asStringArray(
      safeContent.attorney_summary,
      FALLBACK_CONTENT.attorney_summary,
    ),
    credentials: asStringArray(safeContent.credentials, FALLBACK_CONTENT.credentials),
    services,
    timeline: asStringArray(safeContent.timeline, FALLBACK_CONTENT.timeline),
    contact,
    footer_statement: asNonEmptyString(
      safeContent.footer_statement,
      FALLBACK_CONTENT.footer_statement,
    ),
  };
};

export const getSafeServiceOptions = (services) => {
  const options = asObjectArray(services, FALLBACK_CONTENT.services)
    .map((service) => asNonEmptyString(service?.title))
    .filter(Boolean);

  return options.length ? options : ["General inquiry"];
};

export const FALLBACK_SITE_CONTENT = FALLBACK_CONTENT;